import { useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal, maskWholeNumber, unmaskNumber } from '@givebutter/util';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { faPercent } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    PromoCodeRequest,
    createCampaignPromoCode,
    fetchCampaignItems,
    fetchCampaignPromoCode,
    updateCampaignPromoCode,
} from 'api/campaigns';
import { useDialogRoute } from 'components/dialog-route/DialogRouteContext';
import * as Drawer from 'components/drawer';
import * as Form from 'components/form';
import { zeroSelected } from 'components/form/validations';
import { InputCurrencyIcon } from 'components/input';
import * as Select from 'components/select';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { CampaignItem, PromoCode, PromoCodeType } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface PromoCodeFormData {
    code: string;
    type: PromoCodeType;
    amount: string;
    unlimited: 'unlimited' | 'limited';
    uses: string;
    all_items: 'all' | 'custom';
    items: Select.SelectItemWithLabel<undefined>[];
    active: boolean;
}

const PromoCodeFormDrawer = () => {
    const toast = useToast();
    const { goBack } = useDialogRoute();
    const queryClient = useQueryClient();
    const { promo_code_id } = useParams<{ promo_code_id: string }>();
    const isEditing = !!promo_code_id;
    const promoCodeId = promo_code_id ? Number(promo_code_id) : null;
    const { data: campaign } = useCurrentCampaign();
    const campaignId = campaign!.id;

    const { data: promoCode } = useSuspenseQuery({
        queryKey: campaignQueryKeys.promoCode(campaignId, promoCodeId!),
        queryFn: () =>
            conditionallyFetchOrNull(
                () => fetchCampaignPromoCode(campaignId, promoCodeId!),
                isEditing,
            ),
    });

    const { data: campaignTickets } = useSuspenseQuery({
        queryKey: campaignQueryKeys.itemsFiltered(campaignId, { per_page: 999 }),
        queryFn: () => fetchCampaignItems({ campaignId, type: 'ticket', per_page: 999 }),
    });

    const formMethods = Form.useGivebutterForm<PromoCodeFormData>({
        defaultValues: defaultForm(promoCode, campaignTickets?.data),
    });
    const { control, handleSubmit, reset, resetFormButton, setError, unregister, watch } =
        formMethods;
    const type = watch('type');
    const isUnlimited = watch('unlimited') === 'unlimited';
    const allItems = watch('all_items') === 'all';
    const [shouldExitAfterCreate, setShouldExitAfterCreate] = useState(true);

    const { mutate: createPromoCodeMutation, isPending: isCreating } = useMutation({
        mutationFn: (data: PromoCodeRequest) => createCampaignPromoCode(campaignId, data),
        onSuccess: () => {
            toast.success('Promo code created.');

            if (shouldExitAfterCreate) {
                goBack();
            } else {
                reset();
            }

            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.promoCodes(campaign.id),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            const data = error.response?.data;
            const errors = data?.errors;

            if (Array.isArray(errors)) {
                for (const field in errors) {
                    setError(field as keyof PromoCodeFormData, {
                        type: 'server',
                        message: errors[field][0],
                    });
                }
            }

            toast.error(data?.message ?? 'Unable to create promo code.');
            resetFormButton();
        },
    });

    const { mutate: updatePromoCodeMutation, isPending: isUpdating } = useMutation({
        mutationFn: (data: PromoCodeRequest) =>
            updateCampaignPromoCode(campaignId, promoCodeId!, data),
        onSuccess: () => {
            toast.success('Promo code updated.');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.promoCodes(campaign.id),
            });
            goBack();
        },
        onError: (error: GivebutterAxiosError) => {
            const data = error.response?.data;
            const errors = data?.errors;

            if (Array.isArray(errors)) {
                for (const field in errors) {
                    setError(field as keyof PromoCodeFormData, {
                        type: 'server',
                        message: errors[field][0],
                    });
                }
            }

            toast.error(data?.message ?? 'Unable to update promo code.');
            resetFormButton();
        },
    });

    const onSubmit: SubmitHandler<PromoCodeFormData> = data => {
        const mutation = isEditing ? updatePromoCodeMutation : createPromoCodeMutation;

        mutation(toRequest(data));
    };

    return (
        <Form.Provider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} className={'contents'}>
                <Drawer.Header>
                    <Drawer.Title>{isEditing ? 'Update' : 'New'} promo code</Drawer.Title>
                    <Drawer.Close />
                </Drawer.Header>
                <Drawer.Body className={'flex flex-col gap-y-6'}>
                    <div>
                        <section className={'flex flex-col gap-y-2'}>
                            <Form.Label name={'code'}>Code name</Form.Label>
                            <Form.UseFormTextInputRoot
                                name={'code'}
                                control={control}
                                rules={{ required: 'The code is required.' }}
                            >
                                <Form.TextInputForm
                                    placeholder={'Enter code (e.g. 5OFF, GIVE, FREE)'}
                                />
                            </Form.UseFormTextInputRoot>
                            <Form.Error name={'code'} />
                        </section>
                    </div>

                    <section className={'flex flex-col gap-y-2'}>
                        <div className={'grid grid-cols-2 items-end gap-x-4'}>
                            <div className={'col-span-1'}>
                                <Form.Label name={'type'}>Type</Form.Label>
                            </div>
                            <div className={'col-span-1'}>
                                <Form.Label name={'amount'}>Amount</Form.Label>
                            </div>
                        </div>

                        <div className={'grid grid-cols-2 gap-x-4'}>
                            <div className={'col-span-1 flex flex-col gap-y-2'}>
                                <Form.SingleSelect
                                    control={control}
                                    name="type"
                                    rules={{ required: 'The type is required.' }}
                                    onValueChange={() => unregister('amount')}
                                >
                                    <SingleSelect.Trigger aria-label="Type" />
                                    <SingleSelect.Content>
                                        <SingleSelect.Item value="fixed">
                                            <SingleSelect.ItemText>
                                                Fixed amount
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="percentage">
                                            <SingleSelect.ItemText>
                                                Percentage
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    </SingleSelect.Content>
                                </Form.SingleSelect>
                                <Form.Error name={'type'} />
                            </div>

                            <div className={'col-span-1 flex flex-col gap-y-2'}>
                                {type === 'fixed' && (
                                    <Form.TextInputRoot
                                        name={'amount'}
                                        control={control}
                                        rules={{ required: 'The amount is required.' }}
                                        inputRootProps={{
                                            mask: 'decimal',
                                        }}
                                    >
                                        <InputCurrencyIcon />
                                        <Form.TextInputForm placeholder={'25'} />
                                    </Form.TextInputRoot>
                                )}
                                {type === 'percentage' && (
                                    <Form.TextInputRoot
                                        name={'amount'}
                                        control={control}
                                        rules={{
                                            required: 'The amount is required.',
                                            max: {
                                                value: 100,
                                                message: 'The maximum amount is 100%.',
                                            },
                                        }}
                                        inputRootProps={{
                                            mask: 'whole-number',
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPercent}
                                            className={'text-gray-400'}
                                        />
                                        <Form.TextInputForm placeholder={'25'} />
                                    </Form.TextInputRoot>
                                )}
                                <Form.Error name={'amount'} />
                            </div>
                        </div>
                    </section>

                    <section className={'flex flex-col gap-y-2'}>
                        <div className={'grid grid-cols-2 gap-x-4'}>
                            <div className={'col-span-1'}>
                                <Form.Label name={'unlimited'}>Quantity</Form.Label>
                            </div>
                            <div className={'col-span-1'}>
                                {!isUnlimited && (
                                    <Form.Label name={'uses'} className={'sr-only'}>
                                        Quantity value
                                    </Form.Label>
                                )}
                            </div>
                        </div>

                        <div className={'grid grid-cols-2 gap-x-4'}>
                            <div className={'col-span-1 flex flex-col gap-y-2'}>
                                <Form.SingleSelect
                                    control={control}
                                    name="unlimited"
                                    rules={{ required: 'Quantity type is required.' }}
                                >
                                    <SingleSelect.Trigger aria-label="Quantity type" />
                                    <SingleSelect.Content>
                                        <SingleSelect.Item value="unlimited">
                                            <SingleSelect.ItemText>Unlimited</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="limited">
                                            <SingleSelect.ItemText>Limit to</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    </SingleSelect.Content>
                                </Form.SingleSelect>
                            </div>

                            {!isUnlimited && (
                                <div className={'col-span-1 flex flex-col gap-y-2'}>
                                    <Form.UseFormTextInputRoot
                                        name={'uses'}
                                        control={control}
                                        rules={{
                                            required: !isUnlimited
                                                ? 'Must provide the number of times this promo code can be used.'
                                                : false,
                                            min: {
                                                value: 1,
                                                message:
                                                    'Must provide the number of times this promo code can be used.',
                                            },
                                        }}
                                        inputRootProps={{
                                            mask: 'whole-number',
                                        }}
                                    >
                                        <Form.TextInputForm placeholder={'100'} />
                                    </Form.UseFormTextInputRoot>
                                </div>
                            )}
                        </div>

                        <div>
                            <Form.Error name={'uses'}>
                                <Form.ErrorMessage />
                                <Form.ErrorValid>
                                    <p className={'col-span-full text-sm text-gray-600'}>
                                        Total number of times this promo code can be used.
                                    </p>
                                </Form.ErrorValid>
                            </Form.Error>
                        </div>
                    </section>

                    <section className={'flex flex-col gap-y-2'}>
                        <div>
                            <Form.Label name={'all_items'}>Tickets to discount</Form.Label>
                        </div>

                        <div className={'grid grid-cols-2 gap-x-4'}>
                            <div className={'col-span-1'}>
                                <Form.SingleSelect
                                    control={control}
                                    name="all_items"
                                    rules={{ required: 'Selection is required.' }}
                                >
                                    <SingleSelect.Trigger aria-label="Tickets to discount" />
                                    <SingleSelect.Content>
                                        <SingleSelect.Item value="all">
                                            <SingleSelect.ItemText>
                                                All tickets
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="custom">
                                            <SingleSelect.ItemText>Custom</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    </SingleSelect.Content>
                                </Form.SingleSelect>
                            </div>

                            {!allItems && (
                                <div className={'col-span-1'}>
                                    <Select.UseFormSearchableSelect
                                        name={`items`}
                                        multi={true}
                                        showTags={false}
                                        showInnerSearch={true}
                                        control={control}
                                        rules={{ validate: zeroSelected('ticket') }}
                                    >
                                        <Select.AllListItem />
                                        {campaignTickets?.data.map(ticket => (
                                            <Select.ListItem
                                                key={ticket.id}
                                                value={{
                                                    label:
                                                        ticket.name.length > 8
                                                            ? ticket.name.slice(0, 8) + '...'
                                                            : ticket.name,
                                                    id: ticket.id.toString(),
                                                }}
                                                checkable={true}
                                            >
                                                {ticket.name}
                                            </Select.ListItem>
                                        ))}
                                    </Select.UseFormSearchableSelect>
                                </div>
                            )}
                        </div>

                        <div className={'grid grid-cols-2 gap-x-4'}>
                            <div className={'col-span-1'}>
                                <Form.Error name={'all_items'} />
                            </div>

                            <div className={'col-span-1'}>
                                <Form.Error name={'items'} />
                            </div>
                        </div>
                    </section>

                    <section className={'col-span-2 flex justify-between gap-y-1'}>
                        <div className={'flex flex-col gap-y-1'}>
                            <Form.Label name={'active'}>Active</Form.Label>
                            <p className={'text-gray-600'}>Is your promo code active?</p>
                        </div>
                        <Form.Switch name={'active'} control={control} />
                    </section>

                    <section
                        className={'bg-gray-25 flex gap-x-3 rounded-lg border border-gray-300 p-3'}
                    >
                        <FontAwesomeIcon
                            icon={faInfoCircle}
                            className={'flex-shrink-0 text-base text-gray-500'}
                        />
                        <p className={'-mt-0.5 text-sm leading-5 text-gray-600'}>
                            Promo codes apply to each ticket, not the entire order. When supporters
                            purchase multiple tickets with a promo code, the discount is applied to
                            each ticket individually.
                        </p>
                    </section>
                </Drawer.Body>
                <Drawer.Footer>
                    <div
                        className={
                            'flex w-full flex-wrap-reverse items-center justify-between gap-2'
                        }
                    >
                        <Drawer.Cancel asChild={true}>
                            <Button variant="transparent">Cancel</Button>
                        </Drawer.Cancel>
                        <div className={'flex flex-wrap-reverse items-center gap-4'}>
                            {!isEditing && (
                                <Form.SubmitButton
                                    variant={'outline'}
                                    color={'neutral'}
                                    noAnimation={true}
                                    onClick={() => setShouldExitAfterCreate(false)}
                                    isSubmitting={isCreating}
                                >
                                    Create and add another
                                </Form.SubmitButton>
                            )}
                            <Form.SubmitButton
                                onClick={() => setShouldExitAfterCreate(true)}
                                isSubmitting={isCreating || isUpdating}
                            >
                                {isEditing ? 'Update' : 'Create'}
                            </Form.SubmitButton>
                        </div>
                    </div>
                </Drawer.Footer>
            </form>
        </Form.Provider>
    );
};

const defaultForm = (
    promoCode: PromoCode | null,
    campaignTickets: CampaignItem[] | undefined,
): PromoCodeFormData => {
    if (!promoCode) {
        return {
            code: '',
            type: 'percentage',
            amount: '',
            unlimited: 'unlimited',
            uses: '',
            all_items: 'all',
            items: [],
            active: true,
        };
    }

    return {
        code: promoCode.code,
        type: promoCode.type,
        amount:
            promoCode.type === 'fixed'
                ? maskDecimal(promoCode.amount)
                : maskWholeNumber(promoCode.amount),
        unlimited: promoCode.uses === null ? 'unlimited' : 'limited',
        uses: maskWholeNumber(promoCode.uses || 0),
        all_items: promoCode.all_items ? 'all' : 'custom',
        items: promoCode.all_items
            ? []
            : campaignTickets
                  ?.filter(t => promoCode.items?.includes(t.id))
                  ?.map(t => ({ id: t.id.toString(), label: t.name, value: undefined })) || [],
        active: promoCode.active,
    };
};

const toRequest = (form: PromoCodeFormData): PromoCodeRequest => {
    const { unlimited, all_items, ...rest } = form;

    return {
        ...rest,
        items: all_items === 'all' ? null : rest.items.map(t => Number(t.id)),
        uses: unlimited === 'unlimited' ? null : unmaskNumber(form.uses),
        amount: unmaskNumber(form.amount),
    };
};

export { PromoCodeFormDrawer };
